import { api, queryString, type Items, type Paginated } from '@sentinel/web-core';
import type {
  MeshTracker,
  MissionEvent,
  TeamCandidate,
  TeamUpdate,
  TeamBoard,
  TeamCreate,
  TeamSummary,
  TransferableTeam,
} from '@sentinel/shared';

/** Squadre (Squadre v7): the board of a mission, a new team, one brought from another
 *  operation, and its state. */
export const teamEndpoints = {
  board: (missionId: string) => api<TeamBoard>(`/missions/${missionId}/teams/board`),
  create: (missionId: string, body: TeamCreate) =>
    api<TeamSummary>(`/missions/${missionId}/teams`, { method: 'POST', body }),
  transferable: (missionId: string) =>
    api<Items<TransferableTeam>>(`/missions/${missionId}/teams/transferable`),
  transfer: (missionId: string, teamId: string) =>
    api<TeamSummary>(`/missions/${missionId}/teams/transfer`, {
      method: 'POST',
      body: { teamId },
    }),
  /** «Collega un nuovo tracker»: a node heard on the network becomes a tracker of the mission. */
  registerTracker: (missionId: string, nodeId: string, label: string) =>
    api<MeshTracker>(`/missions/${missionId}/mesh-nodes/${nodeId}/tracker`, {
      method: 'POST',
      body: { label },
    }),
  setStatus: (missionId: string, teamId: string, statusKey: string) =>
    api<TeamSummary>(`/missions/${missionId}/teams/${teamId}/status`, {
      method: 'PATCH',
      body: { statusKey },
    }),
  /** The team sheet: counts, sector, channel; people; tracker; its chronicle. */
  update: (missionId: string, teamId: string, body: TeamUpdate) =>
    api<TeamSummary>(`/missions/${missionId}/teams/${teamId}`, { method: 'PATCH', body }),
  setTracker: (missionId: string, teamId: string, deviceId: string | null) =>
    api<TeamSummary>(`/missions/${missionId}/teams/${teamId}/tracker`, {
      method: 'PUT',
      body: { deviceId },
    }),
  candidates: (missionId: string, params: { q?: string; kind: 'person' | 'dog' }) =>
    api<Items<TeamCandidate>>(`/missions/${missionId}/teams/candidates${queryString(params)}`),
  addMember: (missionId: string, teamId: string, personId: string) =>
    api<TeamSummary>(`/missions/${missionId}/teams/${teamId}/members`, {
      method: 'POST',
      body: { personId },
    }),
  removeMember: (missionId: string, teamId: string, personId: string) =>
    api<TeamSummary>(`/missions/${missionId}/teams/${teamId}/members/${personId}`, {
      method: 'DELETE',
    }),
  events: (missionId: string, teamId: string, params: { page: number; pageSize: number }) =>
    api<Paginated<MissionEvent>>(
      `/missions/${missionId}/teams/${teamId}/events${queryString(params)}`,
    ),
  dissolve: (missionId: string, teamId: string) =>
    api<undefined>(`/missions/${missionId}/teams/${teamId}`, { method: 'DELETE' }),
  restore: (missionId: string, teamId: string) =>
    api<TeamSummary>(`/missions/${missionId}/teams/${teamId}/restore`, { method: 'POST' }),
};
